/** A parsed payload node, ready to render. */
export type JsonNode =
    | { kind: 'primitive'; type: 'string' | 'number' | 'boolean' | 'null'; value: string }
    | { kind: 'object'; entries: { key: string; node: JsonNode }[] }
    | { kind: 'array'; items: JsonNode[] }
    /** A string whose contents were themselves JSON, decoded rather than shown escaped. */
    | { kind: 'embedded'; node: JsonNode }

/**
 * Tool results routinely arrive as JSON wrapped in a string inside more JSON — an MCP call comes
 * back as `{content:[{type:'text',text:'{"issues":[...]}'}]}`. Rendered literally that is a wall of
 * backslash-escaped quotes, which is exactly what a run payload looked like before this. Decoding
 * the inner document is most of the readability win, so it happens during parsing rather than being
 * left to the eye.
 */
function looksLikeJson(value: string): boolean {
    const trimmed = value.trim()
    if (trimmed.length < 2) return false
    const first = trimmed[0]
    const last = trimmed[trimmed.length - 1]
    return (first === '{' && last === '}') || (first === '[' && last === ']')
}

function build(value: unknown, depth: number): JsonNode {
    // A guard against a pathological payload, not a display limit: nesting this deep is a bug.
    if (depth > 32) return { kind: 'primitive', type: 'string', value: String(value) }
    if (value === null) return { kind: 'primitive', type: 'null', value: 'null' }
    if (Array.isArray(value)) return { kind: 'array', items: value.map(item => build(item, depth + 1)) }
    if (typeof value === 'object') {
        return {
            kind: 'object',
            entries: Object.entries(value as Record<string, unknown>).map(([key, item]) => ({
                key,
                node: build(item, depth + 1),
            })),
        }
    }
    if (typeof value === 'string') {
        if (looksLikeJson(value)) {
            try {
                return { kind: 'embedded', node: build(JSON.parse(value), depth + 1) }
            } catch {
                // Not actually JSON — fall through and show the string.
            }
        }
        return { kind: 'primitive', type: 'string', value }
    }
    if (typeof value === 'number') return { kind: 'primitive', type: 'number', value: String(value) }
    if (typeof value === 'boolean') return { kind: 'primitive', type: 'boolean', value: String(value) }
    return { kind: 'primitive', type: 'string', value: String(value) }
}

/**
 * Parse a payload for rendering. Returns `null` when the input is not JSON at all, which is the
 * signal to fall back to plain text — run logs are not JSON and must still be readable.
 */
export function parsePayload(input: unknown): JsonNode | null {
    if (typeof input === 'string') {
        if (!looksLikeJson(input)) return null
        try {
            return build(JSON.parse(input), 0)
        } catch {
            return null
        }
    }
    if (input === undefined) return null
    return build(input, 0)
}

/** How many direct children a node has, for the collapsed summary. */
export function childCount(node: JsonNode): number {
    if (node.kind === 'object') return node.entries.length
    if (node.kind === 'array') return node.items.length
    if (node.kind === 'embedded') return childCount(node.node)
    return 0
}
